import {useI18n} from 'i18n/I18nContext'
import React, {useCallback} from 'react'
import styled from 'styled-components'
import {Droppable, Draggable} from 'react-beautiful-dnd'
import {cloneBlockWithNewId} from 'TemplateEditor/cloneSectionWithNewIds'
import {BlockElements, ListBlocks} from 'TemplateEditor/Layout/Blocks'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {useTemplate} from 'TemplateEditor/TemplateProvider'
import {Section} from 'TemplateEditor/template'
import {confirmAlert} from 'react-confirm-alert'
import {removeByNestedKeys} from 'utils/lib/json'

export function BlockDrop(props: {
  section: Section
  sectionIndex: number
  index: number
}) {
  const {section, sectionIndex, index: columnIndex} = props
  const {t} = useI18n()
  const {template, updateTemplate} = useTemplate()
  const {
    editMode,
    editElement,
    mobileMode,
    setEditElement,
    clearSelectionAfterMutation,
  } = useEditor()
  const droppableId = `${section.id}.${columnIndex}.block`

  const handleSetEditMode = (editKey: string) => {
    if (!editMode) return

    setEditElement(editKey)
  }

  const handleCopyBlock = useCallback(
    (block: BlockElements, blockIndex: number) => {
      if (!editMode) return

      const sections = template.sections?.length ? [...template.sections] : []
      const sec = {...sections[sectionIndex]}
      const column = [...(sec.blocks[columnIndex] ?? [])]
      const dup = cloneBlockWithNewId(block)
      column.splice(blockIndex + 1, 0, dup)
      sections[sectionIndex] = {
        ...sec,
        blocks: {...sec.blocks, [columnIndex]: column},
      }
      updateTemplate({...template, sections})
    },
    [columnIndex, editMode, sectionIndex, template, updateTemplate],
  )

  const handleRemoveBlock = useCallback(
    (editKey: string) => {
      if (!editMode) return

      confirmAlert({
        message: t('templateEditor.confirmRemoveBlock'),
        closeOnClickOutside: false,
        buttons: [
          {
            label: t('common.yes'),
            onClick: () => {
              const next = removeByNestedKeys(template, editKey)
              updateTemplate(next)
              if (editElement === editKey) {
                clearSelectionAfterMutation(next)
              }
            },
          },
          {
            label: t('common.no'),
            onClick: () => {},
          },
        ],
      })
    },
    [
      clearSelectionAfterMutation,
      editElement,
      editMode,
      t,
      template,
      updateTemplate,
    ],
  )

  const hasItems =
    section.blocks &&
    section.blocks[columnIndex] &&
    section.blocks[columnIndex].length > 0

  return (
    <Droppable
      droppableId={droppableId}
      type={'block'}
      isDropDisabled={!editMode}
    >
      {(provided, snapshot) => {
        return (
          <BlockDropHolder
            {...provided.droppableProps}
            ref={provided.innerRef}
            $isDraggingOver={snapshot.isDraggingOver}
            $editMode={editMode}
            $hasItems={hasItems}
          >
            {section.blocks &&
              section.blocks[columnIndex] &&
              section.blocks[columnIndex].map((block, blockIndex) => {
                const blockDragId =
                  block.id ||
                  `block-fallback-${section.id}-${columnIndex}-${blockIndex}`
                return (
                  <Draggable
                    key={blockDragId}
                    draggableId={blockDragId}
                    index={blockIndex}
                    isDragDisabled={!editMode}
                  >
                    {(dragProvided) => {
                      const editKey = `sections.${sectionIndex}.blocks.${columnIndex}.${blockIndex}`

                      return (
                        <DraggableBlockRoot
                          ref={dragProvided.innerRef}
                          data-template-block-root
                          {...dragProvided.draggableProps}
                        >
                          {editMode && (
                            <BlockHoverToolbar>
                              <BlockRemoveToolbarButton
                                type="button"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                  handleRemoveBlock(editKey)
                                }}
                                title={t('templateEditor.removeBlock')}
                                aria-label={t('templateEditor.removeBlock')}
                              >
                                <i className="fa-light fa-trash-can"></i>
                              </BlockRemoveToolbarButton>
                              <BlockCopyToolbarButton
                                type="button"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                  handleCopyBlock(block, blockIndex)
                                }}
                                title={t('templateEditor.copyBlock')}
                                aria-label={t('templateEditor.copyBlock')}
                              >
                                <i className="fa-light fa-copy"></i>
                              </BlockCopyToolbarButton>
                            </BlockHoverToolbar>
                          )}
                          <DragHandle {...dragProvided.dragHandleProps}>
                            {ListBlocks &&
                              ListBlocks.map((ListBlock) => {
                                if (block.type === ListBlock.typeId) {
                                  return ListBlock.component({
                                    block: block,
                                    section: section,
                                    setEditMode: () => {
                                      handleSetEditMode(editKey)
                                    },
                                    editMode: editMode,
                                    mobileMode: mobileMode,
                                    key: block.id,
                                  })
                                }

                                return null
                              })}
                          </DragHandle>
                        </DraggableBlockRoot>
                      )
                    }}
                  </Draggable>
                )
              })}

            {provided.placeholder}
          </BlockDropHolder>
        )
      }}
    </Droppable>
  )
}

const DraggableBlockRoot = styled.div`
  position: relative;
  z-index: 1;
  isolation: isolate;

  &:hover {
    z-index: 40;
  }
`

const BlockHoverToolbar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 50;
  display: none;
  flex-direction: row;
  align-items: stretch;
  pointer-events: auto;

  ${DraggableBlockRoot}:hover & {
    display: flex;
  }
`

const blockToolbarBtn = styled.button`
  margin: 0;
  padding: 4px 12px;
  min-height: 34px;
  min-width: 38px;
  color: white;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  border: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > i {
    line-height: 1;
    display: block;
  }
`

const BlockRemoveToolbarButton = styled(blockToolbarBtn)`
  background: #c62828;
  border-bottom-left-radius: 4px;
`

const BlockCopyToolbarButton = styled(blockToolbarBtn)`
  background: #5c6bc0;
`

const DragHandle = styled.div`
  width: 100%;
`

const BlockDropHolder = styled.div<{
  $isDraggingOver: boolean
  $editMode: boolean
  $hasItems: boolean
}>`
  min-height: ${(props) =>
    props.$editMode && !props.$hasItems ? '40px' : '0px'};
  height: 100%;

  border-right: ${(props) => (props.$editMode ? '1px grey dashed' : '')};
  border-left: ${(props) => (props.$editMode ? '1px grey dashed' : '')};
  background: ${(props) =>
    props.$isDraggingOver
      ? '#ffdbdb'
      : !props.$hasItems && props.$editMode
        ? 'repeating-linear-gradient(135deg, #ffe4e4 0%, #ffe4e4 1%, transparent 1%, transparent 2%)'
        : 'inherit'};
`
